import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PlayCircle, CheckCircle2, Award, MapPin, Clock } from "lucide-react";
import { api, apiErr, openPdf } from "@/lib/api";
import { toast } from "sonner";
import { Loading, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OnbBadge } from "./AdminOnboarding";

export default function StaffOnboarding() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [openItem, setOpenItem] = useState(null);
  const load = useCallback(() => api.get("/onboarding/my").then(({ data }) => setD(data)), []);
  useEffect(() => { load(); }, [load]);

  const confirmItem = async (itemId) => {
    try { await api.post(`/onboarding/my/orientation/${itemId}/confirm`); toast.success("本人確認しました"); setOpenItem(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!d) return <Loading />;
  const eo = d.onboarding;
  if (!eo) return <EmptyState title="入社研修の対象ではありません" hint="担当者にお問い合わせください" />;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">入社研修</h1>
        <p className="text-sm text-slate-500 mt-1">入社時に必要な研修とオリエンテーションです</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-blue-50 to-white p-5 mb-6" data-testid="staff-onb-summary">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2"><OnbBadge status={eo.status} /><span className="text-sm text-slate-500 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />受講期限 {eo.due_date || "-"}</span></div>
            <div className="mt-2 text-2xl font-bold text-slate-800">{eo.done_items || 0} / {eo.total_items || 0} 研修 修了</div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">{eo.progress || 0}%</div>
            {eo.cert_no && <Button size="sm" variant="outline" className="mt-2 text-amber-700" onClick={() => openPdf(`/onboarding/employees/${d.target.id}/certificate.pdf`)} data-testid="staff-onb-cert"><Award className="w-4 h-4 mr-1" />修了証</Button>}
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-white overflow-hidden"><div className="h-full bg-blue-500 transition-all" style={{ width: `${eo.progress || 0}%` }} /></div>
      </div>

      <h2 className="font-semibold text-slate-800 mb-2">対象研修</h2>
      <div className="space-y-2 mb-8">
        {[...d.trainings].sort((a, b) => (a.status === "修了") - (b.status === "修了")).map((x) => (
          <div key={x.training_id} className="flex items-center justify-between bg-white rounded-xl border border-slate-200 p-4" data-testid={`staff-onb-tr-${x.training_id}`}>
            <div className="min-w-0">
              <div className="font-medium text-slate-800">{x.name}{x.required && <span className="text-xs text-rose-600 ml-2">必須</span>}</div>
              <div className="mt-0.5"><OnbBadge status={x.status} /></div>
            </div>
            {x.status === "修了" ? (
              <span className="text-green-600 flex items-center gap-1 text-sm"><CheckCircle2 className="w-5 h-5" />修了</span>
            ) : (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate(`/learn/${x.training_id}`)} data-testid={`staff-onb-take-${x.training_id}`}><PlayCircle className="w-4 h-4 mr-1" />受講する</Button>
            )}
          </div>
        ))}
        {!d.trainings.length && <p className="text-sm text-slate-400">対象研修はありません</p>}
      </div>

      {d.orientation.length > 0 && (
        <>
          <h2 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><MapPin className="w-4 h-4 text-blue-600" />現場オリエンテーション</h2>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {d.orientation.map((o) => {
              const done = o.status === "本人確認済み" || o.status === "管理者確認済み";
              return (
                <div key={o.item_id} className="flex items-center justify-between px-4 py-3" data-testid={`staff-ori-${o.item_id}`}>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className={`w-5 h-5 ${done ? "text-green-500" : "text-slate-300"}`} />
                    <span className="text-sm text-slate-700">{o.name}</span>
                    <span className="text-xs text-slate-400">{o.status}</span>
                  </div>
                  {o.status !== "管理者確認済み" && (
                    <Button size="sm" variant={done ? "outline" : "default"} className={done ? "" : "bg-blue-600 hover:bg-blue-700"} onClick={() => setOpenItem(o)} data-testid={`staff-ori-confirm-${o.item_id}`}>
                      {done ? "内容を確認" : "説明を読んで確認"}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={!!openItem} onOpenChange={(o) => !o && setOpenItem(null)}>
        <DialogContent data-testid="staff-ori-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPin className="w-4 h-4 text-blue-600" />{openItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed max-h-[52vh] overflow-y-auto" data-testid="staff-ori-dialog-desc">
            {openItem?.description?.trim()
              ? openItem.description
              : "この項目について、説明を受けてください。内容を確認できたら「確認しました」を押してください。"}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenItem(null)} data-testid="staff-ori-dialog-close">閉じる</Button>
            {openItem?.status !== "管理者確認済み" && (
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => confirmItem(openItem.item_id)} data-testid="staff-ori-dialog-confirm">
                <CheckCircle2 className="w-4 h-4 mr-1" />確認しました
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
